import moment from 'src/app/utils/moment-timezone';
import { RRule, RRuleSet, Frequency, ByWeekday, Options } from 'rrule'
import { DateTime } from 'luxon';

export class  Appointment {
  assignedPackage?: string;
  eventType: "appointment" | "session" | "reminder" | "event";
  lastModifiedDate?: string;
  lastModifiedBy?: string;
  periodEnd?: number;
  id: string = null;
  start: string;
  invitees: {
    [userId: string]: {
      type: "organizer" | "invitee",
      status?: "new" | "accepted" | "declined";
    }
  };
  creationDate: string;
  createdBy: string;
  title: string;
  location: string;
  description: string;
  paymentIntent: string;
  paymentStatus: "incomplete" | "canceled" | "paid";
  read?: boolean;
  organization?: string;
  status: "scheduled" | "canceled" | "complete";
  timezone: string;
  isRecurring: boolean;
  color?: string;
  duration?: number;
  durationQuantity?: number;
  durationUnit?: "minutes" | "hours";
  hidden?: boolean;
  recurring?: {
    frequency: "daily" | "weekly" | "monthly";
    interval: number;
    until?: string;
    byDay?: ByWeekday[];
    byMonthDay?: number[];
    exclusions?: {
      [exclusionId: string]: {
        timestamp: string;
      }
    }
  };
  parent?: {
    id: string,
    occurrence: string,
  };

  constructor (init: Partial<Appointment>){
      Object.assign(this, init);
  }

  asEvent(): any {
    // Return events in UTC ISO format - FullCalendar will convert to display timezone
    return {
        id: this.id,
        start: (moment as any)(this.start).utc().toISOString(), // Always UTC
        end: (moment as any)(this.start).utc().add(this.duration, "minutes").toISOString(), // Always UTC
        duration: this.duration,
        title: this.title,
        status: this.status,
        location: this.location,
        type: "appointment",
        eventType: this.eventType,
        color: this.color? this.color : "#2B2E34",
        read: this.read,
        isRecurring: this.isRecurring,
        invitees: this.invitees,
        timezone: this.timezone
    };  
  }

  getRecurringExlusions(): string[] {
    if(this.recurring && this.recurring.exclusions && Object.keys(this.recurring.exclusions).length > 0) {
      const exclusions = this.recurring.exclusions;
      const toReturn = [];
      for (const id in exclusions) {
        if (exclusions.hasOwnProperty(id)) {
            const exclusionObj  = exclusions[id];
            toReturn.push(exclusionObj.timestamp);
        }
    }
      return toReturn
    } else {
      return null
    }
  }

  getRecurringText(): string {
    function getUntilDate(until: string): Date {
      if(until) {
        return (moment as any)(until).toDate()
      } else {
        return null
      }
    }

    function getFreq(frequency: string): Frequency {
      switch (frequency) {
        case "daily":
          return 3
        case "weekly":
          return 2
        case "monthly":
          return 1
      }
    }

    function toInt(input: ByWeekday[]): number[] {
      if(!input || input.length === 0) {
        return null
      } else {
        const toReturn = [];
        for (const val of input) {
          switch (val) {
            case "MO":
              toReturn.push(0);
              break;
            case "TU":
              toReturn.push(1);
              break;
            case "WE":
              toReturn.push(2);
              break;
            case "TH":
              toReturn.push(3);
              break;
            case "FR":
              toReturn.push(4);
              break;
            case "SA":
              toReturn.push(5);
              break;
            case "SU":
              toReturn.push(6);
              break;
            default:
              break;
          }
        }
        return toReturn
      }
    }

    const ruleOptions: any = {
      freq: getFreq(this.recurring.frequency),
      interval: this.recurring.interval,
      dtstart: (moment as any)(this.start).local().toDate(),
      until: getUntilDate(this.recurring.until)
    };
    if(this.recurring.frequency === "weekly") {
      ruleOptions.byweekday = toInt(this.recurring.byDay);
    }
    if(this.recurring.frequency === "monthly") {
      ruleOptions.bymonthday = this.recurring.byMonthDay
    }
    const rule = new RRule(ruleOptions, true);

    return rule.toText()
  }

  getNextOccurrence() {
    const occurrences = this.getOccurrences();
    if(occurrences?.length > 0) {
      const filtered = occurrences.filter(occ => {
        return (moment as any)().isBefore((moment as any)(occ.start));
      });
      return filtered[0]
    } else {
      return null
    }
  }

  getOccurrences(): any[] {
    if(!this.isRecurring || !this.recurring) {
      return []
    }

    function getUntilDate(until: string): Date {
      if(until) {
        return (moment as any)(until).toDate()
      } else {
        return (moment as any)().add(12, "months").toDate()
      }
    }

    function toInt(input: ByWeekday[]): number[] {
      if(!input || input.length === 0) {
        return null
      } else {
        const toReturn = [];
        for (const val of input) {
          switch (val) {
            case "MO":
              toReturn.push(0);
              break;
            case "TU":
              toReturn.push(1);
              break;
            case "WE":
              toReturn.push(2);
              break;
            case "TH":
              toReturn.push(3);
              break;
            case "FR":
              toReturn.push(4);
              break;
            case "SA":
              toReturn.push(5);
              break;
            case "SU":
              toReturn.push(6);
              break;
            default:
              break;
          }
        }
        return toReturn
      }
    }

    function getFreq(frequency: string): Frequency {
      switch (frequency) {
        case "daily":
          return 3
        case "weekly":
          return 2
        case "monthly":
          return 1
      }
    }

    // Use appointment's stored timezone so expansion is correct for conflict detection (e.g. trainer PST vs client EST)
    const timezone = this.timezone || moment.tz.guess();

    // Get the original start time, adjusting for time zone and DST
    const originalStartTime = DateTime.fromISO(this.start, { zone: timezone }).set({ second: 0, millisecond: 0 });

    const rruleSet = new RRuleSet(true)
    const ruleOptions: Partial<Options> = {
      freq: getFreq(this.recurring.frequency),
      interval: this.recurring.interval,
      dtstart: originalStartTime.toJSDate(),
      until: getUntilDate(this.recurring.until),
    }
    if(this.recurring.frequency === "weekly") {
      ruleOptions.byweekday = toInt(this.recurring.byDay);
    }
    if(this.recurring.frequency === "monthly") {
      ruleOptions.bymonthday = this.recurring.byMonthDay
    }
    rruleSet.rrule(new RRule(ruleOptions, true));

    const exclusions = this.getRecurringExlusions();
    if (exclusions && exclusions.length > 0) {
      for (const exclusion of exclusions) {
        const exUtc = DateTime.fromISO(exclusion, { zone: 'utc' }).toJSDate();
        rruleSet.exdate(exUtc);
      }
    }

    const dates = rruleSet.all();
    if(dates && dates.length > 0) {
      const toReturn = [];
      for (const date of dates) {

        // Adjust each occurrence to the same local time as the original start
        const dt = DateTime.fromJSDate(date).setZone(timezone).set({
          hour: originalStartTime.hour,
          minute: originalStartTime.minute,
          second: 0,
          millisecond: 0
        });

        const eventEndTime = dt.plus({ minutes: this.duration });

        const event = this.asEvent();
        event.start =  dt.toISO();
        event.end = eventEndTime.toISO();
        event.startOriginal = this.start;
        event.startUTC = date.toISOString();
        toReturn.push(event);
      }
      return toReturn;
    }
    return [];
  }

  getInvitees(): string[] {
    return this.invitees? Object.keys(this.invitees) : null;
  }

  getOrganizer() {
    let toReturn;
    if(this.invitees) {
      for (const userId in this.invitees) {
        if (Object.prototype.hasOwnProperty.call(this.invitees, userId)) {
          const invitee = this.invitees[userId];
          if(invitee.type === "organizer") {
            toReturn = userId;
            break
          }
        }
      }
    }
    return toReturn
  }

  isUserOrganizer(userId: string) {
    return (this.invitees && this.invitees[userId] && this.invitees[userId].type === "organizer") || (this.eventType === "reminder" && this.createdBy === userId)
  }
}